/**
 * Conversation page — /brand/conversations/:personaId (docs/features/conversations.md §5.13).
 *
 * ONE screen since 2026-11-02: it replaces the persona page and the run page. The URL
 * carries the state (`?day=&engine=&place=&run=`); the old run URL redirects here.
 *
 *   header (Relancer · Dupliquer · Configuration · Modifier) + launch progress
 *   date band — the days of `rollup.launchByDay`, presence only where exact (14-day spark)
 *   ┌ engine switch · place · run squares ┐ ┌ Intention observée, tour par tour ┐
 *   │ the conversation in the engine app  │ │ the current turn (follows scroll)  │
 *   └─────────────────────────────────────┘ └ « Voir sources · cibles · … »      ┘
 *
 * Reads (all existing endpoints, nothing new on the API):
 *   …/personas                         the conversation (header, cells, zone labels)
 *   …/personas/:id/rollup              `launchByDay` — the band's days and launch ids
 *   …/conversations/personas           the summary's 14-day `spark` (exact day presence)
 *   …/launches/:launchId/matrix        the day's cells (engines, places, runs, analysis status)
 *   …/cells/:cellPath/conversations/:i the run's turns (the phone + the right column)
 *   …/cells/:cellPath/sources          RUNX#, folded for the engine / place / day scopes
 *   …/personas/:id/extras?window=      the whole-conversation scope
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../services/api';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';
import {
  CONVERSATION_INTENTS,
  cellPath as buildCellPath,
  launchTs,
  sameEntityName,
  type ConversationEngine,
  type ConversationPersona,
  type ConversationPersonaRollup,
  type ConversationPersonaSummary,
  type ConversationSubRun,
  type ConversationSubRunExtras,
  type ConversationTurn,
  type ConversationIntentId,
} from '@traaker/shared';
import { PromptStudioDialog } from '../components/prompt-studio/PromptStudioDialog';
import { RunSquare, squareDesc, squareKind } from '../components/conversations/RunSquare';
import { CompetitorFollowDialog } from '../components/conversations/CompetitorFollowDialog';
import { hasLaunchToday } from '../components/conversations/supersede';
import { useBrandPersonas } from '../components/conversations/useBrandPersonas';
import { engineDisplay, engineLogoId, pct } from '../components/conversations/personaFormat';
import { EngineLogo } from '../components/brand/EngineLogo';
import { Flag } from './dashboard/Flag';
import {
  TERMINAL_STATUSES,
  availableScopes,
  bandDays,
  dropOutTurnOf,
  foldExtras,
  foldTurns,
  isFinalized,
  parsePlaceKey,
  parseViewParams,
  placeKeyOf,
  placeLabel,
  runRedirectParams,
  sparkByDay,
  utcDay,
  viewParamsToSearch,
  type ScopeId,
  type ViewParams,
} from '../components/conversations/view/conversationView';
import { DateBand } from '../components/conversations/view/DateBand';
import { EnginePhone } from '../components/conversations/view/EnginePhone';
import { IntentsPanel, TurnCard } from '../components/conversations/view/TurnPanels';
import { ResponseDialog } from '../components/conversations/view/ResponseDialog';
import { ScopesDialog, type ScopeOption, type ScopeResult } from '../components/conversations/view/ScopesDialog';

const INTENT_COLORS: Record<ConversationIntentId, string> = {
  learn: '#0f7c8c',
  discover: '#c47a12',
  compare: '#b83280',
  review: '#3d64c8',
};

const PANEL_SX = {
  bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2,
  boxShadow: '0 1px 2px rgba(23,21,29,.05), 0 4px 16px rgba(23,21,29,.05)',
} as const;

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6875rem', display: 'block' }}>
      {children}
    </Typography>
  );
}

/** GET .../launches/:launchId/progress */
interface LaunchProgress {
  launchId: string;
  done: boolean;
  finalizing: boolean;
  totalTurns: number;
  doneTurns: number;
  cells: Array<{ cellKey: string; engine: string; status: string; runs: number; maxTurns: number; completedRuns: number; turnsDone: number; finalized: boolean }>;
}

/** GET .../personas/:personaId/extras?window= — only the fields the popup folds. */
interface WindowExtras {
  days: number;
  launches: number;
  isFloor: boolean;
  topDomains: ConversationSubRunExtras['topDomains'];
  topTargets: ConversationSubRunExtras['topTargets'];
  mentionCounts: Record<string, number>;
  residualDomains: { count: number; cit: number };
  residualTargets: { count: number; cit: number };
}

type MatrixRow = ConversationSubRun;

const rowPlace = (r: MatrixRow) => placeKeyOf(r.zone, r.language);
const rowRuns = (r: MatrixRow) => (Array.isArray(r.runs) ? r.runs.length : r.config?.runs ?? 0);

/** The old run URL → this page, same day / engine / place / run. */
export function ConversationRunRedirect() {
  const { personaId, launchId, cellPath, idx } = useParams<{ personaId: string; launchId: string; cellPath: string; idx: string }>();
  const params = runRedirectParams(launchId ?? '', cellPath ?? '', idx ?? '');
  return <Navigate to={`/brand/conversations/${personaId}${viewParamsToSearch(params)}`} replace />;
}

export default function ConversationPersonaPage() {
  const { t, i18n } = useTranslation();
  const { personaId } = useParams<{ personaId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeBrand: brand } = useBrand();
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const brandId = brand?.brandId;

  const [persona, setPersona] = useState<ConversationPersona | null>(null);
  const [rollup, setRollup] = useState<ConversationPersonaRollup | null>(null);
  const [rollupState, setRollupState] = useState<'loading' | 'computing' | 'ready' | 'error'>('loading');
  const [summary, setSummary] = useState<ConversationPersonaSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [activeLaunch, setActiveLaunch] = useState<{ launchId: string; cellCount: number } | null>(null);
  const [progress, setProgress] = useState<LaunchProgress | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // ---- conversation + roll-up -------------------------------------------------------

  const fetchData = useCallback(async (opts?: { silent?: boolean }) => {
    if (!brandId || !personaId) return;
    if (!opts?.silent) setLoading(true);
    try {
      const personas = await api.get<ConversationPersona[]>(`/brands/${brandId}/personas`);
      const found = (personas ?? []).find((p) => p.personaId === personaId);
      if (!found) {
        navigate('/brand/conversations', { replace: true });
        return;
      }
      // A late row may still say 'running' for a launch the poll saw finish.
      setPersona(opts?.silent && found.lastLaunchStatus === 'running' ? { ...found, lastLaunchStatus: 'completed' } : found);
      if (!opts?.silent && found.lastLaunchId && found.lastLaunchStatus === 'running') {
        setActiveLaunch((cur) => cur ?? { launchId: found.lastLaunchId!, cellCount: found.cells.length });
      }
      try {
        if (!opts?.silent) setRollupState('loading');
        setRollup(await api.get<ConversationPersonaRollup>(`/brands/${brandId}/personas/${personaId}/rollup`));
        setRollupState('ready');
      } catch (err) {
        setRollupState(err instanceof ApiError && err.status === 404 ? 'computing' : 'error');
      }
    } catch {
      if (!opts?.silent) navigate('/brand/conversations', { replace: true });
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [brandId, personaId, navigate]);

  useEffect(() => { void fetchData(); }, [fetchData]);

  // The summary's 14-day spark — the only exact per-day presence. Paginated: follow the
  // cursor until this conversation's row is found (a single page is a silent truncation).
  useEffect(() => {
    if (!brandId || !personaId) return;
    let cancelled = false;
    (async () => {
      let cursor: string | null = null;
      for (let page = 0; page < 20; page++) {
        const resp: { items: ConversationPersonaSummary[]; nextCursor: string | null } = await api.get(
          `/brands/${brandId}/conversations/personas${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
        );
        const hit = (resp?.items ?? []).find((s) => s.personaId === personaId);
        if (hit) { if (!cancelled) setSummary(hit); return; }
        cursor = resp?.nextCursor ?? null;
        if (!cursor) return;
      }
    })().catch(() => { /* no exact presence: the band shows dates only */ });
    return () => { cancelled = true; };
  }, [brandId, personaId, refreshKey]);

  // ---- launch progress (Relancer) --------------------------------------------------

  const fetchDataRef = useRef(fetchData);
  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);
  useEffect(() => {
    if (!activeLaunch || !brandId || !personaId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const result = await api.get<LaunchProgress>(`/brands/${brandId}/personas/${personaId}/launches/${activeLaunch.launchId}/progress`);
        if (cancelled) return;
        setProgress((prev) => {
          const same = prev && prev.launchId === result.launchId && prev.done === result.done && prev.finalizing === result.finalizing
            && prev.doneTurns === result.doneTurns && prev.totalTurns === result.totalTurns
            && prev.cells.every((c, i) => result.cells[i]?.status === c.status && result.cells[i]?.turnsDone === c.turnsDone);
          return same ? prev : result;
        });
        if (result.done) {
          setActiveLaunch(null);
          setProgress(null);
          setRefreshKey((k) => k + 1);
          void fetchDataRef.current({ silent: true });
        }
      } catch { /* keep polling */ }
    };
    void poll();
    const timer = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [activeLaunch, brandId, personaId]);

  const handleDuplicate = async () => {
    if (!brandId || !persona) return;
    try {
      const result = await api.post<ConversationPersona>(`/brands/${brandId}/personas/${persona.personaId}/duplicate`, {});
      navigate(`/brand/conversations/${result.personaId}`);
      setToast({ message: t('conversations.toastDuplicated'), severity: 'success' });
    } catch (err) {
      setToast({ message: err instanceof ApiError ? err.message : t('conversations.editorErrorGeneric'), severity: 'error' });
    }
  };

  const handleRunNow = async () => {
    if (!brandId || !persona) return;
    // D62 — a second launch today replaces today's, without a refund.
    if (hasLaunchToday(persona.lastLaunchAt) && !window.confirm(t('conversations.runNowSupersedeConfirm'))) return;
    try {
      // One Idempotency-Key per click (lot I2 8d): one key = one launchId = one reserve.
      const result = await api.post<{ launchId: string; cellCount: number }>(
        `/brands/${brandId}/personas/${persona.personaId}/run`, {}, { 'Idempotency-Key': crypto.randomUUID() },
      );
      setActiveLaunch({ launchId: result.launchId, cellCount: result.cellCount });
      setProgress(null);
      setPersona((prev) => (prev ? { ...prev, lastLaunchId: result.launchId, lastLaunchStatus: 'running' } : prev));
      setToast({ message: t('conversations.toastRunNow'), severity: 'success' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        const resetTime = err.body?.retryAfterMs != null
          ? new Date(Date.now() + err.body.retryAfterMs).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
          : null;
        setToast({ message: resetTime ? t('conversations.editorErrorRateLimitReset', { time: resetTime }) : t('conversations.editorErrorGeneric'), severity: 'error' });
      } else {
        setToast({ message: t('conversations.editorErrorGeneric'), severity: 'error' });
      }
    }
  };

  // ---- the date band ---------------------------------------------------------------

  const params = useMemo(() => parseViewParams(searchParams), [searchParams]);
  const launchByDay = useMemo(() => {
    const out: Record<string, string> = { ...(rollup?.launchByDay ?? {}) };
    // No roll-up yet (or one written before `launchByDay`): the last launch is all we know.
    if (persona?.lastLaunchId && persona.lastLaunchStatus !== 'running') {
      const d = utcDay(launchTs(persona.lastLaunchId));
      if (!out[d]) out[d] = persona.lastLaunchId;
    }
    return out;
  }, [rollup?.launchByDay, persona?.lastLaunchId, persona?.lastLaunchStatus]);
  const measuredDays = useMemo(() => Object.keys(launchByDay).sort(), [launchByDay]);
  const day = params.day && launchByDay[params.day] ? params.day : measuredDays[measuredDays.length - 1] ?? null;
  const days = useMemo(() => bandDays(launchByDay, sparkByDay(summary), day), [launchByDay, summary, day]);
  const launchId = day ? launchByDay[day] : null;

  const setView = useCallback((next: ViewParams) => {
    setSearchParams(viewParamsToSearch(next).replace(/^\?/, ''), { replace: true });
  }, [setSearchParams]);

  // ---- the day's matrix ------------------------------------------------------------

  const [rows, setRows] = useState<MatrixRow[]>([]);
  const [matrixState, setMatrixState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  useEffect(() => {
    if (!brandId || !personaId || !launchId) { setRows([]); setMatrixState('idle'); return; }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    setMatrixState('loading');
    const load = () => {
      api.get<{ persona: ConversationPersona; cells: (MatrixRow | null)[] }>(`/brands/${brandId}/personas/${personaId}/launches/${launchId}/matrix`)
        .then((r) => {
          if (cancelled) return;
          const next = (r?.cells ?? []).filter((c): c is MatrixRow => !!c);
          setRows(next);
          setMatrixState('ready');
          // The counter flips a cell terminal before the finalizer writes its summary, and
          // the analysis writes `analysisStatus` last: keep re-reading (5 s, ≤ 2 min) while
          // a cell is not readable or still being analysed.
          const awaiting = next.some((c) => (TERMINAL_STATUSES.has(c.status) && !isFinalized(c)) || c.analysisStatus === 'pending');
          if (awaiting && attempts++ < 24) timer = setTimeout(load, 5000);
        })
        .catch(() => { if (!cancelled) { setRows([]); setMatrixState('error'); } });
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [brandId, personaId, launchId, refreshKey]);

  /** Engines in display order: the configured ones first, then any the launch had. */
  const engines = useMemo(() => {
    const out: string[] = [];
    for (const c of persona?.cells ?? []) if (!out.includes(c.engine)) out.push(c.engine);
    for (const r of rows) if (!out.includes(r.engine)) out.push(r.engine);
    return out;
  }, [persona?.cells, rows]);
  const ranEngines = useMemo(() => engines.filter((e) => rows.some((r) => r.engine === e)), [engines, rows]);
  const engine = params.engine && ranEngines.includes(params.engine) ? params.engine : ranEngines[0] ?? null;
  const dayPlaces = useMemo(() => {
    const out: string[] = [];
    for (const r of rows) { const k = rowPlace(r); if (!out.includes(k)) out.push(k); }
    return out;
  }, [rows]);
  const enginePlaces = dayPlaces.filter((k) => rows.some((r) => r.engine === engine && rowPlace(r) === k));
  const place = params.place && enginePlaces.includes(params.place) ? params.place : enginePlaces[0] ?? null;
  const cell = rows.find((r) => r.engine === engine && rowPlace(r) === place) ?? null;
  const runs = useMemo(() => (cell && Array.isArray(cell.runs) ? [...cell.runs].sort((a, b) => a.idx - b.idx) : []), [cell]);
  const runIdx = params.run !== undefined && runs.some((r) => r.idx === params.run) ? params.run : runs[0]?.idx ?? null;
  const run = runs.find((r) => r.idx === runIdx) ?? null;
  const cellPathStr = cell ? buildCellPath(cell.engine as ConversationEngine, cell.language, cell.zone) : null;

  const zoneMetaOf = useCallback((zone: string | undefined) => (persona?.cells ?? []).find((c) => c.zone === zone)?.zoneMeta, [persona?.cells]);
  const placeName = useCallback((key: string) => {
    const p = parsePlaceKey(key);
    return p ? placeLabel(zoneMetaOf(p.zone), p.zone, t('conversations.view.world')) : key;
  }, [zoneMetaOf, t]);

  const enginePresence = (e: string): number | null => {
    let present = 0;
    let total = 0;
    for (const r of rows) if (r.engine === e && r.stats) { present += r.stats.sumPresentTurns; total += r.stats.sumTurns; }
    return total > 0 ? present / total : null;
  };

  // ---- the run's turns -------------------------------------------------------------

  const transcripts = useRef(new Map<string, ConversationTurn[]>());
  const [turns, setTurns] = useState<ConversationTurn[] | null>(null);
  const [turnsState, setTurnsState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const cellAnalysis = cell?.analysisStatus;
  useEffect(() => {
    if (!brandId || !personaId || !launchId || !cellPathStr || runIdx === null || !isFinalized(cell) || (run && squareKind(run) === 'fail')) {
      setTurns(null); setTurnsState('idle'); return;
    }
    const key = `${launchId}|${cellPathStr}|${runIdx}|${cellAnalysis ?? ''}`;
    const cached = transcripts.current.get(key);
    if (cached) { setTurns(cached); setTurnsState('ready'); return; }
    let cancelled = false;
    setTurnsState('loading');
    api.get<{ turns: ConversationTurn[] }>(`/brands/${brandId}/personas/${personaId}/launches/${launchId}/cells/${cellPathStr}/conversations/${runIdx}`)
      .then((r) => {
        if (cancelled) return;
        const sorted = [...(r?.turns ?? [])].sort((a, b) => a.turn - b.turn);
        transcripts.current.set(key, sorted);
        if (transcripts.current.size > 8) transcripts.current.delete(transcripts.current.keys().next().value as string);
        setTurns(sorted);
        setTurnsState('ready');
      })
      .catch(() => { if (!cancelled) { setTurns(null); setTurnsState('error'); } });
    return () => { cancelled = true; };
    // `cell` identity changes on every matrix re-read; its analysis status is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, personaId, launchId, cellPathStr, runIdx, cellAnalysis, cell?.status, run?.outcome]);

  const safeTurns = turns ?? [];
  const [activeTurn, setActiveTurn] = useState(1);
  const [scrollTo, setScrollTo] = useState<{ turn: number; nonce: number } | null>(null);
  useEffect(() => { setActiveTurn(safeTurns[0]?.turn ?? 1); }, [turns]); // eslint-disable-line react-hooks/exhaustive-deps
  const goTurn = (n: number) => { setActiveTurn(n); setScrollTo((s) => ({ turn: n, nonce: (s?.nonce ?? 0) + 1 })); };
  const dropOut = useMemo(() => dropOutTurnOf(safeTurns), [safeTurns]);

  const [responseTurn, setResponseTurn] = useState<number | null>(null);
  const [scopesOpen, setScopesOpen] = useState(false);
  const [follow, setFollow] = useState<{ label: string; domains: string[] } | null>(null);
  const [addedBrands, setAddedBrands] = useState<string[]>([]);

  const brandRef = useMemo(() => ({ name: brand?.name ?? '', aliases: ((brand?.strategy?.aliases ?? []) as string[]) }), [brand?.name, brand?.strategy?.aliases]);
  const isTracked = useCallback((name: string) => (brand?.competitors ?? []).some((c) => sameEntityName(c, name)), [brand?.competitors]);

  // ---- the popup's scopes -----------------------------------------------------------

  const engineRows = rows.filter((r) => r.engine === engine);
  const placeRows = rows.filter((r) => rowPlace(r) === place);
  const sumRuns = (list: MatrixRow[]) => list.reduce((a, r) => a + rowRuns(r), 0);
  const dayLabel = day ? new Date(`${day}T12:00:00Z`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '';
  const scopeIds = availableScopes({ engineRuns: sumRuns(engineRows), placeRuns: sumRuns(placeRows), dayRuns: sumRuns(rows), places: dayPlaces.length });
  const placeMeta = place ? zoneMetaOf(parsePlaceKey(place)?.zone) : undefined;
  const scopeOptions: ScopeOption[] = scopeIds.map((id) => {
    switch (id) {
      case 'run':
        return { id, title: t('conversations.view.scopeRun'), sub: t('conversations.view.scopeRunSub', { engine: engine ? engineDisplay(engine) : '', n: (runIdx ?? 0) + 1, place: place ? placeName(place) : '' }) };
      case 'engine':
        return {
          id,
          title: <><EngineLogo engine={engineLogoId(engine ?? '')} size={16} />{engine ? engineDisplay(engine) : ''}</>,
          sub: t('conversations.view.scopeEngineSub', { count: sumRuns(engineRows), date: dayLabel }),
        };
      case 'place':
        return {
          id,
          title: <>{placeMeta?.countryCode && <Flag countryCode={placeMeta.countryCode.toLowerCase()} size={16} />}{place ? placeName(place) : ''}</>,
          sub: t('conversations.view.scopePlaceSub', { count: sumRuns(placeRows), engines: new Set(placeRows.map((r) => r.engine)).size, date: dayLabel }),
        };
      case 'day':
        return { id, title: t('conversations.view.scopeDay'), sub: t('conversations.view.scopeDaySub', { date: dayLabel }) };
      default:
        return { id, title: t('conversations.view.scopeAll'), sub: t('conversations.view.scopeAllSub') };
    }
  });

  const sourcesCache = useRef(new Map<string, Promise<ConversationSubRunExtras | null>>());
  const loadScope = useCallback(async (scope: ScopeId, win: 7 | 30 | 90): Promise<ScopeResult> => {
    if (scope === 'run') {
      return { folded: foldTurns(safeTurns, brandRef), foot: t('conversations.view.footRun', { count: safeTurns.length }) };
    }
    if (scope === 'all') {
      const e = await api.get<WindowExtras>(`/brands/${brandId}/personas/${personaId}/extras?window=${win}d`);
      return {
        folded: foldExtras([e], brandRef, { floor: e.isFloor }),
        foot: t('conversations.view.footAll', { days: e.days, launches: e.launches }),
      };
    }
    const list = scope === 'engine' ? engineRows : scope === 'place' ? placeRows : rows;
    const read = (r: MatrixRow) => {
      const cp = buildCellPath(r.engine as ConversationEngine, r.language, r.zone);
      const key = `${launchId}|${cp}`;
      let p = sourcesCache.current.get(key);
      if (!p) {
        p = api.get<ConversationSubRunExtras | null>(`/brands/${brandId}/personas/${personaId}/launches/${launchId}/cells/${cp}/sources`);
        sourcesCache.current.set(key, p);
        p.catch(() => sourcesCache.current.delete(key));
      }
      return p;
    };
    const settled = await Promise.allSettled(list.map(read));
    const ok = settled.flatMap((s) => (s.status === 'fulfilled' && s.value ? [s.value] : []));
    const skipped = list.length - ok.length;
    const foot = [
      t('conversations.view.footFold', { count: sumRuns(list) }),
      skipped > 0 ? t('conversations.view.footSkipped', { count: skipped }) : '',
    ].filter(Boolean).join(' ');
    return { folded: foldExtras(ok, brandRef, { floor: true }), foot };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeTurns, brandRef, brandId, personaId, launchId, rows, engine, place, t]);

  // ---- derivations for the header --------------------------------------------------

  const intent = persona ? CONVERSATION_INTENTS[persona.intentId] : null;
  const { byId: brandPersonaById } = useBrandPersonas(brandId, { enabled: !!persona?.brandPersonaId });
  const linkedPersona = persona?.brandPersonaId ? brandPersonaById[persona.brandPersonaId] : undefined;
  const headerDescription = persona?.brandPersonaId ? linkedPersona?.description : persona?.description;
  const tier = tenant?.tier;
  const canRunNow = tier === 'enterprise' || tier === 'agency';

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={90} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={560} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }
  if (!persona || !intent) return null;

  const isRunning = activeLaunch?.launchId != null;
  const totalCells = activeLaunch?.cellCount ?? 0;
  const progressPct = progress && progress.totalTurns > 0 ? Math.round((progress.doneTurns / progress.totalTurns) * 100) : 0;

  /** Why there is nothing to read yet — the three states of §4.3, never collapsed into one. */
  const emptyReason = (() => {
    if (rollupState === 'error') return t('conversations.persona.emptyError');
    if (isRunning && progress?.finalizing) return t('conversations.persona.emptyFinalizing');
    if (isRunning) {
      return progress && progress.doneTurns > 0
        ? t('conversations.persona.emptyRunning', { done: progress.doneTurns, total: progress.totalTurns })
        : t('conversations.persona.emptyRunningNoTurns');
    }
    if (rollupState === 'loading') return t('conversations.persona.emptyLoading');
    return t('conversations.persona.emptyNever');
  })();

  const clock = launchId ? new Date(launchTs(launchId)).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }) : '9:41';
  const overlay = (() => {
    if (matrixState === 'loading' && rows.length === 0) return <CircularProgress size={26} />;
    if (matrixState === 'error') return <Typography variant="body2" color="text.secondary">{t('conversations.runNotFound')}</Typography>;
    if (!cell) return <Typography variant="body2" color="text.secondary">{emptyReason}</Typography>;
    if (!isFinalized(cell)) return <Typography variant="body2" color="text.secondary">{t('conversations.runNotFinalized')}</Typography>;
    if (run && squareKind(run) === 'fail') {
      return (
        <Box>
          <Typography fontWeight={700}>{t('conversations.runFailedTitle')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t('conversations.runFailedBody')}</Typography>
        </Box>
      );
    }
    if (turnsState === 'loading') return <CircularProgress size={26} />;
    if (turnsState === 'error') return <Typography variant="body2" color="text.secondary">{t('conversations.transcriptError')}</Typography>;
    return null;
  })();

  return (
    <Box sx={{ p: 3, bgcolor: '#f8f7fc', minHeight: '100vh', mx: -3, px: 3 }}>
      {/* Breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton size="small" onClick={() => navigate('/brand/conversations')}><ArrowBackIcon fontSize="small" /></IconButton>
        <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={() => navigate('/brand/conversations')}>
          {t('conversations.pageTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">›</Typography>
        <Typography variant="body2" fontWeight={600}>{persona.name}</Typography>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
            <Typography variant="h6" fontWeight={700}>{persona.name}</Typography>
            <Chip label={intent.name} size="small" sx={{ fontSize: '0.6875rem', fontWeight: 600, bgcolor: INTENT_COLORS[persona.intentId] + '18', color: INTENT_COLORS[persona.intentId] }} />
          </Box>
          {linkedPersona && (
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.25 }}>
              {t('conversations.personaLinkedChip', { name: linkedPersona.name })}
            </Typography>
          )}
          {headerDescription && (
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {headerDescription}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {isRunning && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
              <CircularProgress size={16} thickness={4} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{t('conversations.progressRunning')}</Typography>
            </Box>
          )}
          <Tooltip title={isRunning ? t('conversations.progressRunNowBusy') : canRunNow ? '' : t('conversations.cardRunNowTooltipTier')}>
            <span>
              <Button variant="outlined" size="small" startIcon={<PlayCircleOutlineIcon />} disabled={!canRunNow || isRunning} onClick={handleRunNow}>
                {t('conversations.personaRunNowBtn')}
              </Button>
            </span>
          </Tooltip>
          <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} disabled={isRunning} onClick={handleDuplicate}>
            {t('conversations.personaDuplicateBtn')}
          </Button>
          <Button variant="outlined" size="small" startIcon={<SettingsOutlinedIcon />} onClick={() => setConfigOpen(true)}>
            {t('conversations.persona.configBtn')}
          </Button>
          <Tooltip title={isRunning ? t('conversations.persona.editDisabledRunning') : ''}>
            <span>
              <Button variant="contained" size="small" startIcon={<EditIcon />} disabled={isRunning} onClick={() => setEditorOpen(true)}>
                {t('conversations.personaEditBtn')}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Launch progress */}
      {isRunning && (
        <Box sx={{ mb: 2.5, p: 2, border: '2px solid', borderColor: 'primary.main', borderRadius: 2, bgcolor: (th) => th.palette.primary.main + '12' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} thickness={5} />
              <Typography variant="body2" fontWeight={600}>
                {t('conversations.progressRunning')}
                {' · '}
                {progress?.finalizing
                  ? t('conversations.progressFinalizing')
                  : progress && progress.doneTurns > 0
                    ? t('conversations.progressTurns', { done: progress.doneTurns, total: progress.totalTurns })
                    : t('conversations.progressWaiting')}
              </Typography>
            </Box>
            {progress && progress.doneTurns > 0 && <Typography variant="caption" color="text.secondary" fontWeight={600}>{progressPct} %</Typography>}
          </Box>
          <LinearProgress variant={progress && progress.doneTurns > 0 ? 'determinate' : 'indeterminate'} value={progressPct} sx={{ borderRadius: 4, height: 6 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
            {(progress?.cells ?? Array.from({ length: totalCells }, () => null)).map((c, i) => {
              const status = c?.status ?? 'queued';
              const label = c
                ? `${engineDisplay(c.engine)} · ${c.cellKey.split('#')[1]} · ${TERMINAL_STATUSES.has(status) ? c.runs * c.maxTurns : Math.min(c.turnsDone, c.runs * c.maxTurns)}/${c.runs * c.maxTurns}`
                : `… ${i + 1}`;
              const color = status === 'completed' ? 'success' : status === 'partial' ? 'warning' : status === 'failed' ? 'error' : 'default';
              return (
                <Chip
                  key={c?.cellKey ?? i}
                  label={label}
                  size="small"
                  color={color as 'success' | 'warning' | 'error' | 'default'}
                  variant={TERMINAL_STATUSES.has(status) ? 'filled' : 'outlined'}
                  icon={<CircularProgress size={10} thickness={5} sx={{ ml: '6px !important', display: TERMINAL_STATUSES.has(status) ? 'none' : undefined }} />}
                  sx={{ fontSize: '0.6875rem', height: 22 }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      {measuredDays.length === 0 ? (
        <Box sx={{ ...PANEL_SX, p: 3 }}>
          <Typography variant="body2" color="text.secondary">{emptyReason}</Typography>
        </Box>
      ) : (
        <>
          <DateBand days={days} selected={day} onSelect={(d) => setView({ day: d, engine: engine ?? undefined, place: place ?? undefined })} />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 380px' }, gap: 2.5, alignItems: 'start' }}>
            {/* The stage: engine switch, place, runs, phone */}
            <Box sx={{ ...PANEL_SX, p: { xs: 1.5, sm: 2.5 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25, mb: 2 }}>
                <Box role="tablist" aria-label={t('conversations.view.enginesAria')} sx={{ display: 'inline-flex', flexWrap: 'wrap', justifyContent: 'center', bgcolor: '#F2F0F7', borderRadius: '12px', p: '4px', gap: '2px' }}>
                  {engines.map((e) => {
                    const ran = ranEngines.includes(e);
                    const sel = e === engine;
                    const p = ran ? enginePresence(e) : null;
                    return (
                      <Tooltip key={e} title={ran ? '' : t('conversations.view.engineNotRun', { engine: engineDisplay(e) })}>
                        <Box
                          component="button"
                          type="button"
                          role="tab"
                          aria-selected={sel}
                          aria-disabled={!ran || undefined}
                          onClick={() => ran && setView({ day: day ?? undefined, engine: e, place: place ?? undefined })}
                          sx={{
                            all: 'unset', display: 'flex', alignItems: 'center', gap: 0.9, px: 1.5, py: 0.9, borderRadius: '9px', fontSize: 13, fontWeight: 600,
                            cursor: ran ? 'pointer' : 'not-allowed', opacity: ran ? 1 : 0.35,
                            bgcolor: sel ? '#fff' : 'transparent', color: sel ? 'text.primary' : 'text.secondary',
                            boxShadow: sel ? '0 1px 3px rgba(23,21,29,.12)' : 'none',
                          }}
                        >
                          <EngineLogo engine={engineLogoId(e)} size={18} />
                          {engineDisplay(e)}
                          {p != null && <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>{pct(p)}</Box>}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'center', fontSize: 13, color: 'text.secondary' }}>
                  {dayPlaces.length > 1 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{t('conversations.view.placeLabel')}</span>
                      <Select
                        size="small"
                        value={place ?? ''}
                        onChange={(ev) => {
                          const next = String(ev.target.value);
                          const keepEngine = rows.some((r) => r.engine === engine && rowPlace(r) === next);
                          const nextEngine = keepEngine ? engine : rows.find((r) => rowPlace(r) === next)?.engine;
                          setView({ day: day ?? undefined, engine: nextEngine ?? undefined, place: next });
                        }}
                        sx={{ fontSize: 13, minWidth: 200 }}
                      >
                        {dayPlaces.map((k) => {
                          const p = parsePlaceKey(k);
                          const meta = zoneMetaOf(p?.zone);
                          return (
                            <MenuItem key={k} value={k}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                {meta?.countryCode && <Flag countryCode={meta.countryCode.toLowerCase()} size={16} />}
                                {placeName(k)} · {p?.language}
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </Box>
                  ) : place ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {placeMeta?.countryCode && <Flag countryCode={placeMeta.countryCode.toLowerCase()} size={16} />}
                      {placeName(place)} · {parsePlaceKey(place)?.language}
                    </Box>
                  ) : null}
                  {runs.length > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <span>{t('conversations.view.runLabel')}</span>
                      {runs.map((r) => (
                        <RunSquare
                          key={r.idx}
                          run={r}
                          selected={r.idx === runIdx}
                          onClick={() => setView({ day: day ?? undefined, engine: engine ?? undefined, place: place ?? undefined, run: r.idx })}
                          label={`${t('conversations.transcriptRun', { n: r.idx + 1 })} — ${squareDesc(r, t)}`}
                        />
                      ))}
                      <Box component="span" sx={{ fontSize: 12 }}>{t('conversations.view.runOf', { n: Math.max(0, runs.findIndex((r) => r.idx === runIdx)) + 1, total: runs.length })}</Box>
                    </Box>
                  )}
                </Box>
              </Box>

              <EnginePhone
                engine={engine ?? 'chatgpt'}
                turns={safeTurns}
                brandId={brandId}
                title={persona.name}
                clock={clock}
                cellAnalysisStatus={cellAnalysis}
                dropOutTurn={dropOut}
                scrollTo={scrollTo}
                onActiveTurn={setActiveTurn}
                onOpenResponse={setResponseTurn}
                overlay={overlay}
              />
            </Box>

            {/* The right column */}
            <Box sx={{ position: { lg: 'sticky' }, top: { lg: 104 }, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              {safeTurns.length > 0 ? (
                <>
                  <IntentsPanel turns={safeTurns} activeTurn={activeTurn} cellAnalysisStatus={cellAnalysis} onPick={goTurn} />
                  <TurnCard turns={safeTurns} activeTurn={activeTurn} cellAnalysisStatus={cellAnalysis} brand={brandRef} dropOutTurn={dropOut} onNav={goTurn} />
                </>
              ) : (
                <Box sx={{ ...PANEL_SX, p: 2.25 }}>
                  <Eyebrow>{t('conversations.view.intentsTitle')}</Eyebrow>
                  <Skeleton variant="rectangular" height={150} sx={{ mt: 1.25, borderRadius: 1 }} animation={turnsState === 'loading' ? 'pulse' : false} />
                </Box>
              )}
              <Button
                variant="outlined"
                disabled={!launchId}
                onClick={() => setScopesOpen(true)}
                sx={{ py: 1.4, borderRadius: '12px', fontWeight: 700, textTransform: 'none', bgcolor: '#F3EEFF', borderColor: 'primary.light', '&:hover': { bgcolor: '#EBE2FF' } }}
              >
                {t('conversations.view.seeAll')}
              </Button>
            </Box>
          </Box>
        </>
      )}

      <ResponseDialog
        open={responseTurn !== null}
        onClose={() => setResponseTurn(null)}
        turn={safeTurns.find((x) => x.turn === responseTurn) ?? null}
        engine={engine ?? 'chatgpt'}
        brandId={brandId}
      />

      <ScopesDialog
        open={scopesOpen}
        onClose={() => setScopesOpen(false)}
        options={scopeOptions}
        load={loadScope}
        isTracked={isTracked}
        followed={addedBrands}
        onFollow={(label, domains) => setFollow({ label, domains })}
      />

      {follow && brandId && (
        <CompetitorFollowDialog
          open
          label={follow.label}
          sourceDomains={follow.domains}
          brandId={brandId}
          personaId={personaId}
          launchId={launchId ?? undefined}
          cellPath={cellPathStr ?? undefined}
          onClose={() => setFollow(null)}
          onSuccess={(label) => {
            setAddedBrands((prev) => [...prev, label]);
            setFollow(null);
            setToast({ message: t('conversations.runToastCompetitorAdded', { brand: label }), severity: 'success' });
          }}
        />
      )}

      {/* Configuration — read-only, opened from the header */}
      <Dialog open={configOpen} onClose={() => setConfigOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.0625rem' }}>{t('conversations.persona.configTitle')}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            <Box>
              <Eyebrow>{t('conversations.persona.configIntent')}</Eyebrow>
              <Typography variant="body2">{intent.name}</Typography>
            </Box>
            <Box>
              <Eyebrow>{t('conversations.persona.configCadence')}</Eyebrow>
              <Typography variant="body2">
                {persona.cadence && persona.cadence !== 'off'
                  ? persona.cadence.interval === 1
                    ? t(`conversations.cadence${persona.cadence.unit === 'days' ? 'Daily' : persona.cadence.unit === 'weeks' ? 'Weekly' : 'Monthly'}`)
                    : t(`conversations.persona.configCadenceEvery${persona.cadence.unit === 'days' ? 'Days' : persona.cadence.unit === 'weeks' ? 'Weeks' : 'Months'}`, { interval: persona.cadence.interval })
                  : t('conversations.persona.configCadenceOff')}
              </Typography>
            </Box>
            <Box>
              <Eyebrow>{t('conversations.persona.configMaxTurns')}</Eyebrow>
              <Typography variant="body2">{persona.maxTurns}</Typography>
            </Box>
            <Box>
              <Eyebrow>{t('conversations.persona.configMode')}</Eyebrow>
              <Typography variant="body2">{t(`conversations.persona.mode${persona.mode.charAt(0).toUpperCase()}${persona.mode.slice(1)}`)}</Typography>
            </Box>
            {persona.nextRunAt && (
              <Box>
                <Eyebrow>{t('conversations.persona.configNextRun')}</Eyebrow>
                <Typography variant="body2">
                  {new Date(persona.nextRunAt).toLocaleString(i18n.language, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
            )}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Eyebrow>{t('conversations.personaCells')}</Eyebrow>
          {persona.cells.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('conversations.persona.configNoCells')}</Typography>
          ) : (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {Object.entries(
                persona.cells.reduce<Record<string, { engine: string; runs: number }[]>>((acc, c) => {
                  const key = `${c.zoneMeta?.label ?? c.zone}|${c.language}`;
                  (acc[key] ??= []).push({ engine: c.engine, runs: c.runs });
                  return acc;
                }, {}),
              ).map(([key, entries]) => {
                const [zoneLabel, lang] = key.split('|');
                return (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={500} sx={{ minWidth: 120 }}>{zoneLabel}</Typography>
                    <Chip label={lang} size="small" variant="outlined" sx={{ fontSize: '0.6875rem', fontFamily: 'monospace' }} />
                    {entries.map((e) => (
                      <Chip key={e.engine} label={`${engineDisplay(e.engine)} × ${e.runs}`} size="small" sx={{ fontSize: '0.6875rem', fontWeight: 600 }} />
                    ))}
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* « Modifier »: Prompt Studio on its Conversations tab, this conversation opened (lot I3, C16). */}
      {brand && (
        <PromptStudioDialog
          open={editorOpen}
          tenant={tenant}
          brand={brand}
          initialTab="conversations"
          initialAxis="narrative"
          initialConversation={persona.personaId}
          onClose={() => setEditorOpen(false)}
          onChanged={() => void fetchData({ silent: true })}
        />
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
